import { vec2 } from "./vec2";
export class box2 {
    /** Translate the given box by the specified vector, and return the new box. */
    static translate(box, by) {
        return { min: vec2.add(box.min, by), max: vec2.add(box.max, by) };
    }
    /** Return the center of the given box */
    static center(box) {
        return {
            x: (box.max.x + box.min.x) / 2,
            y: (box.max.y + box.min.y) / 2,
        };
    }
    static intersect(boxA, boxB) {
        const intersection = {
            max: { x: Math.min(boxA.max.x, boxB.max.x), y: Math.min(boxA.max.y, boxB.max.y) },
            min: { x: Math.max(boxA.min.x, boxB.min.x), y: Math.max(boxA.min.y, boxB.min.y) },
        };
        const size = box2.size(intersection);
        // Intersect of 0 is considered intersecting -- it means the edges are touching or one of the
        // boxes is 0-sized but still in the other box's region. That's different than no intersection.
        return size.x >= 0 && size.y >= 0 ? intersection : undefined;
    }
    static size(box) {
        return vec2.sub(box.max, box.min);
    }
    static area(box) {
        const size = box2.size(box);
        return size.x * size.y;
    }
    /** Return a new box that is expanded (if necessary) to include the given point. */
    static expandByPoint(box, point) {
        return {
            min: { x: Math.min(box.min.x, point.x), y: Math.min(box.min.y, point.y) },
            max: { x: Math.max(box.max.x, point.x), y: Math.max(box.max.y, point.y) },
        };
    }
    static fromPoints(points) {
        if (points.length === 0) {
            return undefined;
        }
        const box = {
            min: { x: +Infinity, y: +Infinity },
            max: { x: -Infinity, y: -Infinity },
        };
        for (const point of points) {
            box.min.x = Math.min(box.min.x, point.x);
            box.min.y = Math.min(box.min.y, point.y);
            box.max.x = Math.max(box.max.x, point.x);
            box.max.y = Math.max(box.max.y, point.y);
        }
        return box;
    }
    /** Return bounds in RBush format */
    static toRBush(box) {
        return { minX: box.min.x, maxX: box.max.x, minY: box.min.y, maxY: box.max.y };
    }
    static fromRBush(box) {
        return {
            min: { x: box.minX, y: box.minY },
            max: { x: box.maxX, y: box.maxY },
        };
    }
    /** Return new box that is expanded by the given scalar amount in each direction. */
    static expandByScalar(box, amount) {
        return {
            min: vec2.subScalar(box.min, amount),
            max: vec2.addScalar(box.max, amount),
        };
    }
    /** Returns a new box that is expanded by the given vector in both directions. */
    static expandByVector(box, amount) {
        return {
            min: vec2.sub(box.min, amount),
            max: vec2.add(box.max, amount),
        };
    }
    static unionBox(...boxes) {
        // Use preallocation when we can, usually offers slight performance edge
        const points = new Array(boxes.length * 2);
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        boxes.forEach(({ min, max }, i) => {
            points[i * 2 + 0] = min;
            points[i * 2 + 1] = max;
        });
        return box2.fromPoints(points);
    }
    /**
     * Return true if the box is valid, meaning:
     * - min and max coordinates are not NaN
     * - min.x <= max.x and min.y <= max.y
     */
    static isValid(box) {
        return (!isNaN(box.min.x) &&
            !isNaN(box.min.y) &&
            !isNaN(box.max.x) &&
            !isNaN(box.max.y) &&
            box.min.x <= box.max.x &&
            box.min.y <= box.max.y);
    }
    /** Convenience function for returning a default valid bounding box in scenarios
     * where it either doesn't matter what it is, or you want a zero-sized box at a
     * particular location.
     */
    static empty(at = { x: 0, y: 0 }) {
        return { min: { ...at }, max: { ...at } }; // Clone `at` to avoid surprises.
    }
    /**
     * Apply a 4x4 matrix to the box, transforming it. The matrix is expected to be in
     * column-major order, as per the `Matrix4Array` type.
     */
    static applyMatrix4(box, matrix) {
        const tl = { x: box.min.x, y: box.max.y };
        const tr = { x: box.max.x, y: box.max.y };
        const bl = { x: box.min.x, y: box.min.y };
        const br = { x: box.max.x, y: box.min.y };
        const transformedTl = vec2.applyMatrix4AsPoint(tl, matrix);
        const transformedTr = vec2.applyMatrix4AsPoint(tr, matrix);
        const transformedBl = vec2.applyMatrix4AsPoint(bl, matrix);
        const transformedBr = vec2.applyMatrix4AsPoint(br, matrix);
        const minX = Math.min(transformedTl.x, transformedTr.x, transformedBl.x, transformedBr.x);
        const minY = Math.min(transformedTl.y, transformedTr.y, transformedBl.y, transformedBr.y);
        const maxX = Math.max(transformedTl.x, transformedTr.x, transformedBl.x, transformedBr.x);
        const maxY = Math.max(transformedTl.y, transformedTr.y, transformedBl.y, transformedBr.y);
        const min = { x: minX, y: minY };
        const max = { x: maxX, y: maxY };
        return {
            min,
            max,
        };
    }
    static chain(b) {
        return {
            translate: (by) => box2.chain(box2.translate(b, by)),
            center: () => box2.center(b),
            intersect: (b2) => {
                const intersect = box2.intersect(b, b2);
                return intersect ? box2.chain(intersect) : undefined;
            },
            size: () => box2.size(b),
            area: () => box2.area(b),
            expandByScalar: (amount) => box2.chain(box2.expandByScalar(b, amount)),
            expandByVector: (amount) => box2.chain(box2.expandByVector(b, amount)),
            expandByPoint: (point) => box2.chain(box2.expandByPoint(b, point)),
            unionBox: (...others) => box2.chain(box2.unionBox(b, ...others)),
            isValid: () => box2.isValid(b),
            toRBush: () => box2.toRBush(b),
            value: () => b,
        };
    }
}
//# sourceMappingURL=box2.js.map